import React, { useState } from 'react';
import { Upload, Button, Modal, Form, Input, Select, Switch, message, Space } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useUploadDocument } from '../../hooks/useDocuments';
import type { Document } from '../../services/document.service';

const { TextArea } = Input;

interface DocumentUploadProps {
  entityType?: string;
  entityId?: string;
  /** Called with the saved document after upload (includes id for linking, e.g. MTC on GRN heat lines). */
  onSuccess?: (document: Document) => void;
  buttonText?: string;
  /** Preset form values when the upload modal opens (e.g. NCR photo evidence). */
  defaultValues?: {
    document_type?: string;
    document_category?: string;
    tags?: string;
    description?: string;
  };
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  entityType,
  entityId,
  onSuccess,
  buttonText = 'Upload Document',
  defaultValues,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const uploadMutation = useUploadDocument();

  const openModal = () => {
    if (defaultValues) {
      form.setFieldsValue(defaultValues);
    }
    setIsModalVisible(true);
  };

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      
      if (fileList.length === 0) {
        message.error('Please select a file to upload');
        return;
      }

      const file = fileList[0].originFileObj;
      if (!file) {
        message.error('Invalid file');
        return;
      }

      const saved = await uploadMutation.mutateAsync({
        file,
        data: {
          document_name: values.document_name || file.name,
          entity_type: entityType,
          entity_id: entityId,
          document_type: values.document_type,
          document_category: values.document_category,
          description: values.description,
          tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : undefined,
          keywords: values.keywords,
          is_confidential: values.is_confidential,
          access_level: values.access_level || 'Private',
        },
      });

      form.resetFields();
      setFileList([]);
      setIsModalVisible(false);
      
      // Trigger custom event to refetch documents
      if (entityType && entityId) {
        const eventName = `refetch-documents-${entityType}-${entityId}`;
        window.dispatchEvent(new Event(eventName));
      }
      
      if (saved) onSuccess?.(saved);
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setIsModalVisible(false);
  };

  const uploadProps = {
    beforeUpload: () => false, // Prevent auto upload
    fileList,
    onChange: ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
      setFileList(newFileList);
    },
    maxCount: 1,
  };

  return (
    <>
      <Button type="primary" icon={<UploadOutlined />} onClick={openModal}>
        {buttonText}
      </Button>

      <Modal
        title="Upload Document"
        open={isModalVisible}
        onOk={handleUpload}
        onCancel={handleCancel}
        confirmLoading={uploadMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="File" required>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="document_name"
            label="Document Name"
            rules={[{ required: true, message: 'Please enter document name' }]}
          >
            <Input placeholder="Enter document name" />
          </Form.Item>

          <Form.Item name="document_type" label="Document Type">
            <Select placeholder="Select document type">
              <Select.Option value="Certificate">Certificate</Select.Option>
              <Select.Option value="Report">Report</Select.Option>
              <Select.Option value="Image">Image</Select.Option>
              <Select.Option value="Drawing">Drawing</Select.Option>
              <Select.Option value="Specification">Specification</Select.Option>
              <Select.Option value="Contract">Contract</Select.Option>
              <Select.Option value="ProductionApprovalForm">Production approval form (signed PDF)</Select.Option>
              <Select.Option value="Other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="document_category" label="Category">
            <Input placeholder="Enter category" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item name="tags" label="Tags (comma-separated)">
            <Input placeholder="e.g., quality, inspection, ncr" />
          </Form.Item>

          <Form.Item name="keywords" label="Keywords">
            <Input placeholder="Enter keywords" />
          </Form.Item>

          <Form.Item name="access_level" label="Access Level" initialValue="Private">
            <Select>
              <Select.Option value="Private">Private</Select.Option>
              <Select.Option value="Internal">Internal</Select.Option>
              <Select.Option value="Public">Public</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="is_confidential" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="Confidential" unCheckedChildren="Not Confidential" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DocumentUpload;

